import { Controller } from "@hotwired/stimulus"

// The DPK photo undo bar and Delete now confirm (owner register 2026-09-11,
// DPK_Photo_Recycle_Bin_Plan.md D3/D4). Two surfaces, one controller:
//
//  * On a photo card: `trash` posts the DELETE at once (the server trashes
//    the row, nothing client-deferred), then swaps the card in place to
//    "Photo deleted. Undo" with a line that slides empty over `seconds`.
//    Undo posts restore and puts the original card back with no reload.
//    When the line runs out the card folds and the row the server rendered
//    lands in the Recently deleted section below. The undo never expires;
//    only the shortcut does (the bin is the durable door).
//  * On a Delete now form in the bin: `confirmPurge` asks through the
//    in-app modal (window.appConfirm; the form is turbo:false, where
//    turbo_confirm is dead) and submits on Delete for good.
export default class extends Controller {
  static values = {
    trashUrl: String,
    restoreUrl: String,
    seconds: { type: Number, default: 8 },
    days: { type: Number, default: 7 },
    purgeMessage: String
  }

  disconnect() {
    if (this._timeout) clearTimeout(this._timeout)
  }

  async trash(event) {
    event.preventDefault()
    // Armed BEFORE the request (reconvene, Seat 1): a fast second click must
    // not send a second DELETE that 404s beside a card reading "Photo deleted."
    if (this._pending) return
    this._pending = true

    let data
    try {
      const response = await this.send(this.trashUrlValue, "DELETE")
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      data = await response.json()
    } catch {
      this._pending = false
      if (window.showToast) window.showToast("error", "Could not delete the photo. Please try again.")
      return
    }

    this._rowHtml = data.row_html
    this._originalHtml = this.element.innerHTML
    // Hold the card's footprint while it is the bar, so a lone card in the
    // last row does not let the bin jump up under it.
    this._minHeight = this.element.style.minHeight
    this.element.style.minHeight = `${this.element.offsetHeight}px`
    // Sortable must not post a trashed id back into the live order.
    this._sortableId = this.element.dataset.sortableId
    delete this.element.dataset.sortableId

    this.element.innerHTML = `
      <div class="flex items-center justify-between gap-3 px-4 py-3">
        <div class="min-w-0">
          <p class="text-sm font-medium text-[var(--color-mc-text)]">Photo deleted.</p>
          <p class="text-xs text-[var(--color-mc-text-secondary)] mt-0.5">Kept in Recently deleted below for ${this.daysValue} days.</p>
        </div>
        <button type="button" data-action="click->photo-trash#undo"
                class="text-sm font-medium text-[var(--color-mc-accent-terracotta)] hover:underline cursor-pointer whitespace-nowrap">
          Undo
        </button>
      </div>
      <div class="h-0.5 bg-[var(--color-mc-accent-terracotta)]/20">
        <div data-photo-trash-line class="h-full bg-[var(--color-mc-accent-terracotta)] ease-linear"
             style="width: 100%; transition: width ${this.secondsValue}s linear;"></div>
      </div>
    `

    requestAnimationFrame(() => {
      const line = this.element.querySelector("[data-photo-trash-line]")
      if (line) line.style.width = "0%"
    })
    this._timeout = setTimeout(() => this.fold(), this.secondsValue * 1000)
  }

  async undo(event) {
    if (event) event.preventDefault()
    if (this._timeout) clearTimeout(this._timeout)

    try {
      const response = await this.send(this.restoreUrlValue, "POST")
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
    } catch {
      if (window.showToast) window.showToast("error", "Could not restore the photo. It is still in Recently deleted below.")
      this._timeout = setTimeout(() => this.fold(), 1000)
      return
    }

    this.element.innerHTML = this._originalHtml
    this.element.style.minHeight = this._minHeight || ""
    if (this._sortableId) this.element.dataset.sortableId = this._sortableId
    this._pending = false
  }

  // The card folds shut and its row appears at the top of the bin.
  fold() {
    const bin = document.getElementById("photos-trash")
    if (bin && this._rowHtml) {
      bin.hidden = false
      const rows = bin.querySelector("[data-trash-rows]")
      if (rows) rows.insertAdjacentHTML("afterbegin", this._rowHtml)
      const count = bin.querySelector("[data-trash-count]")
      if (count && rows) count.textContent = rows.children.length
    }
    this.element.remove()
  }

  // The question rides in the message, not a title option: the /pr layout
  // ships the shared confirm dialog, which has no title slot, and a dropped
  // title would leave only the consequences on screen.
  async confirmPurge(event) {
    event.preventDefault()
    const ok = await window.appConfirm(this.purgeMessageValue, {
      confirmLabel: "Delete for good",
      cancelLabel: "Cancel"
    })
    if (ok) event.target.submit()
  }

  send(url, method) {
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content || ""
    return fetch(url, {
      method,
      headers: { "X-CSRF-Token": csrf, "Accept": "application/json" },
      credentials: "same-origin"
    })
  }
}
